import { useLocale } from "@hooks/useLocale";
import { useCurrentWell } from "@hooks/useCurrentWell";
import { QueryState } from "@components/ui/QueryState";
import { useGetWellSurveysQuery } from "@api/pApi";
import { fmtDepth, intlTag } from "@lib/format";
import type { SurveyPassOut, SurveyStationOut } from "@api/types";
import type { Locale } from "@i18n/locales";
import type { MessageKey } from "@i18n/messages";
import { SurveyPlots } from "./SurveyPlots";
import { computeMinCurvature } from "./minCurvature";

/** A survey pass's status → a translated, colour-coded badge label. */
function statusKey(status: string | null | undefined): MessageKey | null {
  if (status === "definitive") return "surveys.status.definitive";
  if (status === "provisional") return "surveys.status.provisional";
  return null;
}

/** Date-only formatter for a survey_date (ISO yyyy-mm-dd), locale-aware. */
function fmtDate(iso: string | null, locale: Locale): string {
  if (!iso) return "—";
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(intlTag(locale), {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/** Angle (inc/azi) with one decimal, no unit suffix (the header carries °). */
function fmtAngle(n: number | null | undefined, locale: Locale): string {
  if (n == null || !Number.isFinite(n)) return "—";
  return n.toLocaleString(intlTag(locale), { maximumFractionDigits: 1 });
}

/** DLS as-served (deg/30m metric · deg/100ft imperial); server-computed, 2dp. */
function fmtDls(n: number | null | undefined, locale: Locale): string {
  if (n == null || !Number.isFinite(n)) return "—";
  return n.toLocaleString(intlTag(locale), { maximumFractionDigits: 2 });
}

/**
 * The station grid shared by a pass and the ungrouped bucket. TVD & DLS are
 * computed client-side by minimum-curvature (minCurvature.ts) so the whole
 * column is populated even where Oxbo-P stored nulls; the station's own tvd, if
 * present, is used only as an out-of-band sanity check (dev console) — the
 * computed value is what we display.
 */
function StationTable({ stations }: { stations: SurveyStationOut[] }) {
  const { t, locale } = useLocale();
  const rows = Array.isArray(stations) ? stations : [];

  // Integrate the run once; index computed geometry by MD (the survey's key).
  const computedByMd = new Map<number, ReturnType<typeof computeMinCurvature>[number]>();
  for (const c of computeMinCurvature(
    rows.map((s) => ({ md: s.md, inclination: s.inclination, azimuth: s.azimuth })),
  )) {
    computedByMd.set(c.md, c);
  }

  if (rows.length === 0) {
    return <div className="empty-state">{t("surveys.pass.noStations")}</div>;
  }
  return (
    <div className="table-scroll">
      <table>
        <thead>
          <tr>
            <th className="num">{t("surveys.col.seq")}</th>
            <th className="num">{t("surveys.col.md")}</th>
            <th className="num">{t("surveys.col.inc")}</th>
            <th className="num">{t("surveys.col.azi")}</th>
            <th className="num">{t("surveys.col.tvd")}</th>
            <th className="num">{t("surveys.col.dls")}</th>
            <th>{t("surveys.col.date")}</th>
            <th>{t("surveys.col.type")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((s) => {
            const c = computedByMd.get(s.md);
            // Prefer the computed value; fall back to any server value only for
            // stations that carried no inc/azi (so couldn't be integrated).
            const tvd = c ? c.tvd : s.tvd;
            const dls = c ? c.dls : s.dogleg_severity;
            return (
              <tr key={s.id} data-owner="p">
                <td className="num tab-nums">{s.sequence_no ?? "—"}</td>
                <td className="num tab-nums">{fmtDepth(s.md, locale)}</td>
                <td className="num tab-nums">{fmtAngle(s.inclination, locale)}</td>
                <td className="num tab-nums">{fmtAngle(s.azimuth, locale)}</td>
                <td className="num tab-nums">{fmtDepth(tvd, locale)}</td>
                <td className="num tab-nums">{fmtDls(dls, locale)}</td>
                <td style={{ whiteSpace: "nowrap" }}>{fmtDate(s.survey_date, locale)}</td>
                <td>{s.station_type ?? "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** One survey pass: a header (method / status / datum / station count) + its stations. */
function SurveyPassCard({ pass }: { pass: SurveyPassOut }) {
  const { t } = useLocale();
  const stations = Array.isArray(pass.stations) ? pass.stations : [];
  const statusMsg = statusKey(pass.pass_status);
  const isDefinitive = pass.pass_status === "definitive";

  return (
    <div className="card" style={{ marginTop: 14 }}>
      <div className="page-head" style={{ marginBottom: 10 }}>
        <div>
          <h3>
            {t("surveys.pass")}
            {pass.method ? (
              <span className="mono" style={{ marginLeft: 8, color: "var(--dim)" }}>
                {pass.method}
              </span>
            ) : null}
          </h3>
          <p className="subtitle" style={{ margin: 0 }}>
            {statusMsg ? (
              <span className={`badge ${isDefinitive ? "status-drilling" : "status-completion"}`}>
                {t(statusMsg)}
              </span>
            ) : (
              <span className="badge">{pass.pass_status ?? "—"}</span>
            )}
            {pass.datum_ref ? (
              <span style={{ marginLeft: 10, color: "var(--dim)" }}>
                {t("surveys.pass.datum")}: <span className="mono">{pass.datum_ref}</span>
              </span>
            ) : null}
            <span style={{ marginLeft: 10, color: "var(--dim)" }}>
              {t("surveys.pass.stationCount", { count: stations.length })}
            </span>
          </p>
        </div>
      </div>
      <StationTable stations={stations} />
      <SurveyPlots stations={stations} />
    </div>
  );
}

/**
 * Surveys (Perfilajes) — a well's directional survey passes and their stations.
 * WELL-scoped: fetches GET /v1/wells/{uwi}/surveys for the currently-selected
 * well. Each pass is grouped/labelled by method + provisional/definitive status;
 * `ungrouped` stations (active, not yet linked to a pass) get their own bucket so
 * none is silently dropped. TVD & DLS are computed CLIENT-SIDE by
 * minimum-curvature (see minCurvature.ts) so the columns are always populated
 * even when Oxbo-P leaves the server-stored values null; per-run plan/section/3D
 * plots are drawn from the same computed points.
 */
export function Surveys() {
  const { t } = useLocale();
  const { well, isLoading: wellLoading, isError: wellError } = useCurrentWell();
  const uwi = well?.uwi ?? "";
  const {
    data,
    isLoading: surveysLoading,
    isError: surveysError,
    refetch,
  } = useGetWellSurveysQuery(uwi, { skip: !uwi });

  const passes = Array.isArray(data?.passes) ? data!.passes : [];
  const ungrouped = Array.isArray(data?.ungrouped) ? data!.ungrouped : [];
  const hasData = passes.length > 0 || ungrouped.length > 0;

  return (
    <div>
      <div className="page-head">
        <h1>{t("surveys.title")}</h1>
        <p className="subtitle">
          {well ? `${well.commonName} · ${well.field ?? "—"}` : t("well.notSelected")}
        </p>
      </div>
      <QueryState
        isLoading={wellLoading || surveysLoading}
        isError={wellError || surveysError}
        isEmpty={!well ? true : !!data && !hasData}
        emptyMessage={!well ? t("well.notSelected") : t("surveys.noData")}
        onRetry={uwi ? refetch : undefined}
      >
        <p className="subtitle" style={{ marginTop: -4 }}>
          {t("surveys.subtitle")}
        </p>

        {passes.map((p) => (
          <SurveyPassCard key={p.id} pass={p} />
        ))}

        {ungrouped.length > 0 ? (
          <div className="card" style={{ marginTop: 14 }}>
            <div className="page-head" style={{ marginBottom: 10 }}>
              <div>
                <h3>{t("surveys.ungrouped")}</h3>
                <p className="subtitle" style={{ margin: 0 }}>
                  {t("surveys.ungrouped.note")}{" "}
                  <span style={{ color: "var(--dim)" }}>
                    {t("surveys.pass.stationCount", { count: ungrouped.length })}
                  </span>
                </p>
              </div>
            </div>
            <StationTable stations={ungrouped} />
            <SurveyPlots stations={ungrouped} />
          </div>
        ) : null}
      </QueryState>
    </div>
  );
}
